import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiLink, FiCheckCircle, FiXCircle, FiExternalLink, FiHelpCircle } = FiIcons;

const ManageRecommendations = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [questionCounts, setQuestionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRec, setCurrentRec] = useState(null);
  
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(0); // 0 or 1

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: function() {
          const range = this.quill.getSelection();
          const value = prompt('Bitte geben Sie die Bild-URL ein:');
          if(value) {
            this.quill.insertEmbed(range.index, 'image', value, 'user');
          }
        }
      }
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recRes, relRes] = await Promise.all([
        apiService.getRecommendations(),
        apiService.getQuestionRecommendationRelations()
      ]);

      if (recRes.status === 'success') {
        setRecommendations(recRes.data);
      }

      if (relRes.status === 'success' && Array.isArray(relRes.data)) {
        const counts = {};
        relRes.data.forEach(rel => {
          const recId = rel.recommendation_id;
          counts[recId] = (counts[recId] || 0) + 1;
        });
        setQuestionCounts(counts);
      } else {
        setQuestionCounts({});
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        title, 
        short_description: shortDescription,
        description,
        is_active: isActive
      };
      
      if (currentRec) {
        await apiService.updateRecommendation(currentRec.id, payload);
      } else {
        await apiService.createRecommendation(payload);
      }

      fetchData();
      closeModal();
    } catch (error) {
      console.error('Error saving recommendation:', error);
      alert('Fehler beim Speichern der Empfehlung');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Empfehlung wirklich löschen?')) {
      try {
        const relationsRes = await apiService.getQuestionRecommendationRelations({ recommendation_id: id });
        if (relationsRes.status === 'success') {
            await Promise.all(relationsRes.data.map(r => apiService.deleteQuestionRecommendationRelation(r.id)));
        }
        
        await apiService.deleteRecommendation(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting recommendation:', error);
        alert('Fehler beim Löschen der Empfehlung');
      }
    }
  };

  const toggleStatus = async (rec) => {
    try {
      const newStatus = rec.is_active === 1 ? 0 : 1;
      await apiService.updateRecommendation(rec.id, {
        title: rec.title, 
        description: rec.description,
        short_description: rec.short_description,
        is_active: newStatus
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Fehler beim Ändern des Status');
    }
  };

  const openModal = (rec = null) => {
    if (rec) {
      setCurrentRec(rec);
      setTitle(rec.title);
      setShortDescription(rec.short_description || '');
      setDescription(rec.description);
      setIsActive(rec.is_active !== undefined ? rec.is_active : 1);
    } else {
      setCurrentRec(null);
      setTitle('');
      setShortDescription('');
      setDescription('');
      setIsActive(1); 
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentRec(null);
    setTitle('');
    setShortDescription('');
    setDescription('');
    setIsActive(0);
  };

  const handleAssignQuestions = (id) => {
    navigate(`/admin/recommendations/${id}/questions`);
  };

  if (loading) return <div>Laden...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Empfehlungen verwalten</h2>
        <div className="flex gap-3">
          <a
            href="#/public/recommendations"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            <SafeIcon icon={FiExternalLink} className="mr-2" /> Öffentliche Seite
          </a>
          <button
            onClick={() => {
                setIsActive(1); 
                openModal();
            }}
            className="flex items-center px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark transition-colors"
          >
            <SafeIcon icon={FiPlus} className="mr-2" /> Neue Empfehlung
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {recommendations.map((rec) => {
            const count = questionCounts[rec.id] || 0;
            return (
              <li key={rec.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-3">
                      <h3 className="text-lg font-medium text-brand-dark truncate">{rec.title}</h3>
                      {rec.is_active === 1 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Aktiv
                          </span>
                      ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Inaktiv
                          </span>
                      )}
                      {/* Badge for question count */}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`} title={`${count} verknüpfte Fragen`}>
                         <SafeIcon icon={FiHelpCircle} className="w-3 h-3 mr-1" />
                         {count} {count === 1 ? 'Frage' : 'Fragen'}
                      </span>
                  </div>
                  {rec.short_description && (
                    <p className="text-sm text-gray-600 mb-1">{rec.short_description}</p>
                  )}
                </div>
                <div className="flex space-x-3 items-center">
                  <button
                    onClick={() => toggleStatus(rec)}
                    className={`flex items-center text-sm font-medium ${
                        rec.is_active === 1 ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title={rec.is_active === 1 ? "Deaktivieren" : "Aktivieren"}
                  >
                    {rec.is_active === 1 ? (
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
                    ) : (
                        <SafeIcon icon={FiXCircle} className="w-5 h-5" />
                    )}
                  </button>
                  <div className="h-4 w-px bg-gray-300 mx-2"></div>
                  <button
                    onClick={() => handleAssignQuestions(rec.id)}
                    className="text-brand-primary hover:text-brand-dark flex items-center"
                    title="Fragen zuordnen"
                  >
                    <SafeIcon icon={FiLink} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => openModal(rec)}
                    className="text-brand-secondary hover:text-brand-dark"
                    title="Bearbeiten"
                  >
                    <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(rec.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Löschen"
                  >
                    <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                  </button>
                </div>
              </li>
            );
          })}
          {recommendations.length === 0 && (
            <li className="px-6 py-4 text-center text-gray-500">Keine Empfehlungen vorhanden</li>
          )}
        </ul>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8 flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">
                {currentRec ? 'Empfehlung bearbeiten' : 'Neue Empfehlung'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <form id="recForm" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="flex items-center mb-4">
                    <input
                        id="isActive"
                        type="checkbox"
                        className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                        checked={isActive === 1}
                        onChange={(e) => setIsActive(e.target.checked ? 1 : 0)}
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                        Aktiv (im Handbuch sichtbar)
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kurzbeschreibung (Vorschau)</label>
                    <textarea
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                      placeholder="Eine kurze Zusammenfassung, die im Handbuch angezeigt wird."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Detaillierte Beschreibung (HTML)</label>
                    <div className="bg-white">
                      <ReactQuill 
                        theme="snow" 
                        value={description} 
                        onChange={setDescription}
                        modules={modules}
                        formats={formats}
                        className="h-64 mb-12"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                form="recForm"
                className="px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRecommendations;